// 261010 video-ui — dòng «Video N» ở bước 8 (THUẦN, không antd): trạng thái · ảnh đại diện · nút tạo · tóm tắt rỗng · thẻ bản clip.
// Dữ liệu chỉ lấy từ cái FE đã có: `workflow-status.events[]` (thẻ + steps) và chi tiết video (`FilmEvent.clips`). Không bịa giá, không gọi thêm API.
import type { FilmEvent } from '../../types/film';
import type { WorkflowEventStatus } from '../../types/filmWorkflow';
import type { StateTone } from './workflowBoardUtils';
import { clipFailInfo, latestFailedClip } from './clipErrorText';
import { clipVersionsOf } from './shotCameraUtils';

export type VideoRowKind = 'SELECTED' | 'RUNNING' | 'ERROR' | 'STALE' | 'UNPICKED' | 'NONE';
export interface VideoRowStatus {
  kind: VideoRowKind;
  /** Chữ cho `StatusIcon` (RULES của workflowStatusIcons chọn biểu tượng): xong ✓ · đang chạy ⏳ · lỗi ✕ · chưa có ○ · cũ ⟳ · chờ chọn (cảnh báo). */
  chipText: string;
  tone: StateTone;
  headline: string;
  versionCount: number;
  done: number; pending: number; failed: number;
  noPrompt: boolean;
}

const httpUrl = (u: unknown): string | null => (typeof u === 'string' && /^https?:\/\//i.test(u.trim()) ? u.trim() : null);
const noPromptLine = (): string => 'Chưa có prompt — tạo prompt trước';

type ClipLike = NonNullable<FilmEvent['clips']>[number];
const selectedDoneClip = (ev: FilmEvent | null | undefined): ClipLike | null => {
  if (!ev?.selectedClipId) return null;
  const c = (ev.clips ?? []).find((x) => x.id === ev.selectedClipId);
  return c && c.status === 'DONE' ? c : null;
};

/** Video chưa có prompt: đã nạp chi tiết ⇒ theo chữ prompt thật; chưa nạp ⇒ theo ô bước Prompt của workflow-status. */
function noPromptOf(event: WorkflowEventStatus, ev: FilmEvent | null | undefined): boolean {
  if (ev) return !String(ev.authorPrompt ?? '').trim();
  return (event.steps?.PROMPT ?? 'NOT_READY') === 'NOT_READY';
}

export function videoRowStatusOf(event: WorkflowEventStatus, ev?: FilmEvent | null): VideoRowStatus {
  const clips = ev?.clips ?? [];
  const done = clips.filter((c) => c.status === 'DONE').length;
  const pending = clips.filter((c) => c.status === 'PENDING').length;
  const failed = clips.filter((c) => c.status === 'FAILED').length;
  const stepState = event.steps?.VIDEO ?? 'NOT_READY';
  const selected = ev ? selectedDoneClip(ev) !== null : !!event.card?.selectedClip?.url;
  const noPrompt = noPromptOf(event, ev) && !selected && clips.length === 0;
  const base = { versionCount: clips.length, done, pending, failed, noPrompt };

  if (ev ? pending > 0 : stepState === 'RUNNING') {
    const frac = clips.length > 1 && pending > 0 ? ` ${done}/${clips.length}` : '';
    return { ...base, kind: 'RUNNING', chipText: `đang chạy${frac}`, tone: 'doing', headline: 'Đang tạo clip…' };
  }
  if (selected) {
    if (stepState === 'STALE') return { ...base, kind: 'STALE', chipText: 'cũ', tone: 'warn', headline: 'Đã chọn bản nhưng bản này đã cũ — đầu vào đổi sau lần tạo' };
    const headline = clips.length ? `${clips.length} bản · đã chọn` : 'Đã chọn bản';
    return { ...base, kind: 'SELECTED', chipText: 'xong', tone: 'ok', headline };
  }
  if (ev && done > 0) return { ...base, kind: 'UNPICKED', chipText: 'chờ chọn', tone: 'warn', headline: `${done + failed} bản · chưa chọn bản nào` };
  if (stepState === 'ERROR' || (ev && failed > 0)) {
    const info = clipFailInfo(latestFailedClip(clips));
    return { ...base, kind: 'ERROR', chipText: 'lỗi', tone: 'bad', headline: info?.label ?? 'Tạo clip lỗi' };
  }
  return { ...base, kind: 'NONE', chipText: 'chưa có', tone: 'todo', headline: noPrompt ? noPromptLine() : 'Chưa tạo clip' };
}

export type VideoRowThumb = { kind: 'CLIP'; url: string; fallback: string | null } | { kind: 'IMAGE'; url: string };

/** Ảnh nhỏ bên phải dòng: có clip đang chọn ⇒ khung đầu của clip (kèm ảnh dự phòng); không thì ảnh storyboard/sơ đồ của thẻ. */
export function videoRowThumbOf(event: WorkflowEventStatus, ev?: FilmEvent | null): VideoRowThumb | null {
  const img = httpUrl(event.card?.thumb?.url);
  const clipUrl = selectedClipUrlOf(event, ev);
  if (clipUrl) return { kind: 'CLIP', url: clipUrl, fallback: img };
  return img ? { kind: 'IMAGE', url: img } : null;
}

/** URL clip đang chọn để phát (nút ▶): chi tiết trước, rơi về thẻ khi chưa nạp chi tiết. */
export function selectedClipUrlOf(event: WorkflowEventStatus, ev?: FilmEvent | null): string | null {
  if (ev) return httpUrl(selectedDoneClip(ev)?.videoUrl);
  return httpUrl(event.card?.selectedClip?.url);
}

export interface ClipCard {
  id: string; label: string; kind: 'DONE' | 'RUNNING' | 'ERROR' | 'OTHER'; selected: boolean; url: string | null; durationS: number | null;
  /** Chữ ngắn dưới thẻ (lỗi) — không lộ mã thô. */
  note: string | null;
  canPick: boolean; canPlay: boolean;
}
/** Thẻ nhỏ 9:16 của dải bản clip: bản đang dùng lên đầu, rồi bản mới nhất. */
export function clipCardsOf(ev: FilmEvent | null | undefined): ClipCard[] {
  if (!ev) return [];
  const byId = new Map((ev.clips ?? []).map((c) => [c.id, c]));
  return clipVersionsOf(ev, 999).versions.map((v) => {
    const kind: ClipCard['kind'] = v.status === 'DONE' ? 'DONE' : v.status === 'PENDING' ? 'RUNNING' : v.status === 'FAILED' ? 'ERROR' : 'OTHER';
    const usable = kind === 'DONE' && !!v.url;
    return {
      id: v.id, label: v.label, kind, selected: v.selected, url: v.url, durationS: v.durationS,
      note: kind === 'ERROR' ? (clipFailInfo(byId.get(v.id))?.label ?? 'Gen lỗi') : kind === 'RUNNING' ? 'Đang tạo…' : kind === 'OTHER' || (kind === 'DONE' && !v.url) ? 'Chưa có file' : null,
      canPick: usable && !v.selected, canPlay: usable,
    };
  });
}
